import React, { ReactElement, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { BottomSheet } from 'react-spring-bottom-sheet';
import { useMediaQuery } from 'react-responsive';
import { useMst } from '../../../models/stores/Root';
import CartToggleButton from '../../input/CartToggleButton';
import CartTotalSummary from '../../summary/CartTotalSummary';
import CartItemSummary from '../../summary/CartItemSummary';
import CloseIcon from '../../content/CloseIcon';
import PromoCode from '../../input/PromoCode';
import AppliedDiscountsList from '../../summary/AppliedDiscountsList';

export const CartToggleTestId = 'carttoggle-framework';

type CartHeaderProps = {
  onCloseClicked: () => void;
};

const CartHeader: React.FC<CartHeaderProps> = ({
  onCloseClicked,
}: CartHeaderProps) => (
  <>
    <div className="flex flex-row justify-center items-center w-full">
      <span className="text-2xl summary-panel-order">Your Cart</span>
    </div>
    <span
      className="m-4 absolute top-3 right-2 mb-5 ml-0 cursor-pointer transition rounded-sm ease-in-out duration-500 hover:bg-gray-900"
      onClick={onCloseClicked}
      onKeyPress={onCloseClicked}
      role="button"
      tabIndex={0}
    >
      <CloseIcon />
    </span>
  </>
);

const CartToggle: React.FC = observer(() => {
  const rootStore = useMst();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { configuration, cart } = rootStore;
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    cart.setCartToggleOpen(isOpen);
  }, [cart, isOpen]);

  const handleDeviceWidthChange = (matches: boolean): void => {
    if (!matches) {
      setIsOpen(false);
    }
  };

  const handleToggleClicked = (): void => setIsOpen(!isOpen);

  const handleCloseClicked = (): void => setIsOpen(false);

  useMediaQuery(
    { query: '(max-width: 767px)' },
    undefined,
    handleDeviceWidthChange
  );

  const handleApplyPromoCodeClicked = async (promoCode: string) => {
    await cart.applyPromoCode(promoCode);
    return cart.getPromoCodeApplyResult(promoCode);
  };

  const handleOnSpringEnd = (): void => {
    if (isOpen) {
      const drawerContainer = document.querySelectorAll('[role="dialog"]');
      if (drawerContainer[0]) {
        drawerContainer[0].classList.add('summary-panel-order');
      }
    }
  };

  const footerElm = (): ReactElement => {
    return (
      <div className="portrait:text-sm">
        <AppliedDiscountsList />
        <PromoCode
          appliedCouponCodes={cart.appliedCouponCodes}
          onPromoCodeApplyClicked={handleApplyPromoCodeClicked}
        />
        <CartTotalSummary
          currencySymbol={configuration.formattedCurrencySymbol}
          discountTotal={cart.discountTotal}
          subTotal={cart.subTotal}
          shippingTotal={
            rootStore.showTaxAndShipping && cart.hasShippingMethodSelected
              ? cart.shippingTotal
              : undefined
          }
          taxTotal={cart.taxTotal}
          grandTotal={cart.totals.grandTotal}
          giftCardAmountUsed={cart.giftCardAmountUsed}
          giftCards={cart.totals.giftCards}
        />
      </div>
    );
  };

  return (
    <div className="cart-toggle-position">
      <CartToggleButton
        isCartOpen={isOpen}
        toggleOpenState={handleToggleClicked}
      >
        {`${configuration.formattedCurrencySymbol}${cart.grandTotal.toFixed(
          2
        )}`}
      </CartToggleButton>
      <div className="summary-panel-order">
        <BottomSheet
          open={isOpen}
          onDismiss={(): void => setIsOpen(false)}
          defaultSnap={({ snapPoints, lastSnap }): number =>
            lastSnap ?? Math.min(...snapPoints)
          }
          snapPoints={({ maxHeight }): number => maxHeight * 0.9}
          header={<CartHeader onCloseClicked={handleCloseClicked} />}
          footer={footerElm()}
          className="summary-panel-order"
          onSpringStart={handleOnSpringEnd}
        >
          <div className="summary-panel-cart border rounded-md py-1 mx-4">
            <CartItemSummary
              currencySymbol={configuration.formattedCurrencySymbol}
            />
          </div>
        </BottomSheet>
      </div>
    </div>
  );
});

export default CartToggle;
